import { Avatar } from '@armhr/ui';
import { ContactPhone, Groups } from '@mui/icons-material';
import {
  Box,
  Card,
  Chip,
  Divider,
  Grid,
  Link,
  Typography,
} from '@mui/material';
import React, { useMemo } from 'react';

import TeamCard from './TeamCard';

interface CompanyTeamSectionProps {
  companyTeam?: CompanyTeamFullConfig | null;
  armhrContacts: PrismTeamContact[];
}

const DEPARTMENT_ICONS: Record<string, React.ReactNode> = {
  payroll: <ContactPhone fontSize="small" />,
  hr: <Groups fontSize="small" />,
};

const getDepartmentIcon = (name: string) => {
  const key = name.toLowerCase().trim();
  return DEPARTMENT_ICONS[key] || <ContactPhone fontSize="small" />;
};

const getFirstName = (fullName: string): string => {
  return fullName.split(' ')[0];
};

interface DepartmentGroup {
  name: string;
  firstNames: string[];
}

const formatFirstNames = (names: string[]): React.ReactNode => {
  if (names.length === 0) return null;
  if (names.length === 1) {
    return (
      <span>
        <span>Email or call </span>
        <strong>{names[0]}</strong>
      </span>
    );
  }
  const last = names[names.length - 1];
  const rest = names.slice(0, -1);
  return (
    <span>
      <span>Email or call </span>
      {rest.map((n, i) => (
        <React.Fragment key={n}>
          <strong>{n}</strong>
          {i < rest.length - 1 ? <span>, </span> : null}
        </React.Fragment>
      ))}
      <span> or </span>
      <strong>{last}</strong>
    </span>
  );
};

const CompanyTeamSection: React.FC<CompanyTeamSectionProps> = ({
  companyTeam,
  armhrContacts,
}) => {
  const departmentGroups = useMemo((): DepartmentGroup[] => {
    if (!companyTeam?.contacts?.length) return [];
    const groupMap = new Map<
      string,
      { displayName: string; firstNames: string[] }
    >();
    for (const contact of companyTeam.contacts) {
      if (!contact.department) continue;
      const key = contact.department.toLowerCase().trim();
      if (!groupMap.has(key)) {
        groupMap.set(key, { displayName: contact.department, firstNames: [] });
      }
      groupMap.get(key)!.firstNames.push(getFirstName(contact.name));
    }
    return Array.from(groupMap.values()).map(({ displayName, firstNames }) => ({
      name: displayName,
      firstNames,
    }));
  }, [companyTeam]);

  if (!companyTeam?.contacts?.length) {
    return <TeamCard teamContacts={armhrContacts} />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Card
        sx={{
          borderRadius: 3,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          background: 'linear-gradient(135deg, #f8fdf9 0%, #ffffff 100%)',
          border: '1px solid #e0f2e9',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 0.5,
          }}
        >
          <Typography variant="h5" fontWeight={700}>
            {companyTeam.section_title}
          </Typography>
          <Chip
            label="Contact First"
            size="small"
            sx={{
              bgcolor: '#e8f5e9',
              color: '#2e7d32',
              fontWeight: 600,
              fontSize: 12,
            }}
          />
        </Box>

        {companyTeam.section_message && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            {companyTeam.section_message}
          </Typography>
        )}

        {departmentGroups.length > 0 && (
          <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
            {departmentGroups.map((group) => (
              <Grid item xs={12} sm={6} key={group.name}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    p: 1.5,
                    bgcolor: '#eef6f0',
                    borderRadius: 2,
                    border: '1px solid #d4edda',
                  }}
                >
                  <Box sx={{ color: '#2e7d32', mt: 0.3 }}>
                    {getDepartmentIcon(group.name)}
                  </Box>
                  <Box>
                    <Typography
                      fontSize={13.5}
                      fontWeight={600}
                      color="#2e7d32"
                    >
                      {group.name} Questions
                    </Typography>
                    <Typography fontSize={12.5} color="text.secondary">
                      {formatFirstNames(group.firstNames)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
          {companyTeam.contacts.map((contact) => (
            <Box
              key={contact.id}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                p: 2,
                bgcolor: 'white',
                borderRadius: 2,
                border: '1px solid #e8f0e8',
              }}
            >
              <Avatar sx={{ width: 40, height: 40 }} name={contact.name} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 0.3,
                  }}
                >
                  <Typography fontWeight={600} fontSize={14} noWrap>
                    {contact.name}
                  </Typography>
                  {contact.department && (
                    <Chip
                      label={contact.department}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: 11,
                        fontWeight: 600,
                        bgcolor: '#e3f2fd',
                        color: '#1565c0',
                      }}
                    />
                  )}
                </Box>
                {contact.title && (
                  <Typography fontSize={12.5} color="text.secondary">
                    {contact.title}
                  </Typography>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mt: 0.5,
                  }}
                >
                  {contact.email && (
                    <Link
                      href={`mailto:${contact.email}`}
                      fontSize={12.5}
                      sx={{ color: 'primary.main' }}
                    >
                      {contact.email}
                    </Link>
                  )}
                  {contact.phone && (
                    <Typography fontSize={12.5} color="text.secondary">
                      {contact.phone}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Armhr Support Team */}
        {armhrContacts.length > 0 && (
          <>
            <Divider sx={{ my: 2.5 }} />
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Armhr Support Team
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  For issues your company team cannot resolve
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1.2,
              }}
            >
              {armhrContacts.map((contact) => (
                <Box
                  key={contact.email}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    background: '#fafbfc',
                    border: '1px solid #f3f4f6',
                    borderRadius: 2,
                    p: 1.5,
                    gap: 2,
                    minWidth: 0,
                  }}
                >
                  <Avatar
                    sx={{ width: 28, height: 28, mt: 0.2 }}
                    name={contact.name}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{ mt: 0.2 }}
                      fontWeight={500}
                      fontSize={13.5}
                      noWrap
                    >
                      {contact.name}
                    </Typography>
                    <Typography fontSize={11.5} color="text.secondary" noWrap>
                      {contact.title}
                    </Typography>
                    <Link
                      href={`mailto:${contact.email}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ fontSize: 12.5 }}
                    >
                      {contact.email}
                    </Link>
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Card>
    </Box>
  );
};

export default CompanyTeamSection;
